import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Pencil, Building2, Repeat, History, Plus } from "lucide-react";
import { format, parseISO } from "date-fns";
import { AddressFields, formatAddress } from "@/components/forms/AddressFields";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { useCRUDMutations } from "@/hooks/useCRUDMutations";
import { RecurringOrdersSubTable } from "@/components/customers/RecurringOrdersSubTable";
import { useTenant } from "@/hooks/useTenant";
import type { Customer } from "@/types/tms";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{value ?? <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

const emptyForm = {
  organization_name: "",
  short_code: "",
  email: "",
  street_address: "",
  city: "",
  province_state: "",
  postal_zip_code: "",
  country: "",
  requires_tender_id: false,
  requires_bol: false,
  requires_tender: false,
  authorized_nvo: false,
};

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTenant } = useTenant();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [isTemplateCreateOpen, setIsTemplateCreateOpen] = useState(false);

  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", id, activeTenant],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await (supabase.from("customers") as any)
        .select("*")
        .eq("id", id as string)
        .maybeSingle();
      if (error) throw error;
      return (data as Customer) ?? null;
    },
  });

  const { updateMutation, deleteMutation } = useCRUDMutations<typeof emptyForm>({
    tableName: "customers",
    queryKey: "customers",
    entityLabel: "Customer",
    onUpdateSuccess: () => setIsEditOpen(false),
    onDeleteSuccess: () => navigate("/customers"),
  });

  const openEdit = () => {
    if (!customer) return;
    setFormData({
      organization_name: customer.organization_name,
      short_code: customer.short_code,
      email: customer.email || "",
      street_address: customer.street_address || "",
      city: customer.city || "",
      province_state: customer.province_state || "",
      postal_zip_code: customer.postal_zip_code || "",
      country: customer.country || "",
      requires_tender_id: customer.requires_tender_id,
      requires_bol: customer.requires_bol,
      requires_tender: customer.requires_tender,
      authorized_nvo: customer.authorized_nvo,
    });
    setIsEditOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customer) return;
    const payload = { ...formData, email: formData.email.trim() || null };
    updateMutation.mutate({ id: customer.id, data: payload as typeof emptyForm });
  };

  if (isLoading) {
    return (
      <AppLayout title="">
        <div className="space-y-6 animate-fade-in">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[280px]" />
          <Skeleton className="h-[240px]" />
        </div>
      </AppLayout>
    );
  }

  if (!customer) {
    return (
      <AppLayout title="Customer Not Found">
        <div className="text-center py-16 space-y-4">
          <p className="text-muted-foreground">This customer could not be found.</p>
          <Button variant="outline" onClick={() => navigate("/customers")}>
            Back to Customers
          </Button>
        </div>
      </AppLayout>
    );
  }

  const billingReqs = [
    customer.requires_tender_id && "Tender ID",
    customer.requires_bol && "BOL",
    customer.requires_tender && "Tender",
  ].filter(Boolean) as string[];

  const fmtTs = (v?: string | null) => {
    if (!v) return null;
    try {
      return format(parseISO(v), "MMM d, yyyy h:mm a");
    } catch {
      return v;
    }
  };

  const breadcrumbTitle = (
    <Breadcrumb>
      <BreadcrumbList className="text-xl font-semibold">
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/customers" className="text-xl font-semibold">Customers</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage className="text-xl font-semibold">
            {customer.organization_name}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <AppLayout
      title=""
      titleExtra={breadcrumbTitle}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={openEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <ConfirmDeleteButton
            onConfirm={() => deleteMutation.mutate(customer.id)}
            entityName="customer"
            entityLabel={customer.organization_name}
          />
        </div>
      }
    >
      <div className="space-y-6 animate-fade-in">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  Customer Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Organization" value={customer.organization_name} />
                <Field
                  label="Short Code"
                  value={<code className="bg-muted px-2 py-1 rounded text-sm">{customer.short_code}</code>}
                />
                <Field
                  label="Email"
                  value={
                    customer.email ? (
                      <a href={`mailto:${customer.email}`} className="text-primary hover:underline">
                        {customer.email}
                      </a>
                    ) : null
                  }
                />
                <Field label="Full Address" value={formatAddress(customer) || null} />
                <Field
                  label="Billing Requirements"
                  value={
                    billingReqs.length ? (
                      <span className="flex flex-wrap gap-1">
                        {billingReqs.map((req) => (
                          <span key={req} className="status-badge status-booked">
                            {req}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )
                  }
                />
                <Field
                  label="Operations"
                  value={
                    customer.authorized_nvo ? (
                      <span className="status-badge status-booked">NVO</span>
                    ) : null
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Repeat className="h-4 w-4 text-muted-foreground" />
                    Recurring Order Templates
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => setIsTemplateCreateOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add order template
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <RecurringOrdersSubTable
                  customerId={customer.id}
                  customerShortCode={customer.short_code}
                  hideHeader
                  createOpen={isTemplateCreateOpen}
                  onCreateOpenChange={setIsTemplateCreateOpen}
                />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <History className="h-4 w-4 text-muted-foreground" />
                  Customer History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Created" value={fmtTs(customer.created_at)} />
                <Field label="Last Updated" value={fmtTs(customer.updated_at)} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="org-name">Organization Name *</Label>
                <Input
                  id="org-name"
                  value={formData.organization_name}
                  onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="short-code">Short Code *</Label>
                <Input
                  id="short-code"
                  value={formData.short_code}
                  onChange={(e) => setFormData({ ...formData, short_code: e.target.value.toUpperCase() })}
                  maxLength={10}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="customer-email">Email</Label>
                <Input
                  id="customer-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@example.com"
                  maxLength={255}
                />
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Address</h4>
              <AddressFields
                prefix="customer"
                values={formData}
                onChange={(field, value) => setFormData({ ...formData, [field]: value })}
              />
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Billing Requirements</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_tender_id"
                    checked={formData.requires_tender_id}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_tender_id: checked === true })}
                  />
                  <Label htmlFor="requires_tender_id" className="font-normal">Requires Tender ID</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_bol"
                    checked={formData.requires_bol}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_bol: checked === true })}
                  />
                  <Label htmlFor="requires_bol" className="font-normal">Requires Bill of Lading (BOL)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_tender"
                    checked={formData.requires_tender}
                    onCheckedChange={(checked) => setFormData({ ...formData, requires_tender: checked === true })}
                  />
                  <Label htmlFor="requires_tender" className="font-normal">Requires Tender</Label>
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Operations</h4>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="authorized_nvo"
                  checked={formData.authorized_nvo}
                  onCheckedChange={(checked) => setFormData({ ...formData, authorized_nvo: checked === true })}
                />
                <Label htmlFor="authorized_nvo" className="font-normal">Authorized NVO</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={updateMutation.isPending}>Update</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
